import { Component, Suspense, type ReactNode } from 'react'

import { t } from '../i18n'
import { Notice, Pending } from '../ui'
import { isChunkLoadError } from './chunk-load'

type Props = { children: ReactNode }
type State = { error: unknown }

/**
 * What a screen draws when its code cannot be fetched (D1-05).
 *
 * Module screens are loaded on demand, and after a deploy the files an open
 * tab still points at are gone. With nothing to catch the rejected import the
 * whole tree unmounted and the page went blank. This says what happened and
 * offers the reload that repairs it.
 *
 * Only that failure is answered here. Any other error is thrown on, so a fault
 * in a screen's own code is not dressed up as an out-of-date page.
 */
export class ChunkErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: unknown): State {
    return { error }
  }

  override render() {
    const { error } = this.state
    if (error === null) return this.props.children
    if (!isChunkLoadError(error)) throw error
    return (
      <main id="main">
        <Notice tone="warn">
          <p>{t('app.chunk_failed')}</p>
          <button type="button" onClick={() => window.location.reload()}>
            {t('app.reload')}
          </button>
        </Notice>
      </main>
    )
  }
}

/**
 * The frame every routed screen loads in: a pending state while its code is
 * fetched, and the explanation above when it cannot be.
 */
export function LazyScreens({ children }: Props) {
  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<Pending />}>{children}</Suspense>
    </ChunkErrorBoundary>
  )
}
